import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, Check, Clock } from 'lucide-react';
import { Button, Tooltip, Whisper } from 'rsuite';
import { useAuth } from '../../../auth/Auth';
import { getTransactionTimeline } from '../../../auth/_requests';
import { TransactionTimeline } from '../../utils/types';
import {
  formatDateToDisplayShortLocalMonth,
  formatNumberWithDecimals,
} from '../../utils/utils';
import {
  REFUND_STATUS_CONFIG,
  getPaymentMode,
  getStatusConfig,
  isRefundTransaction,
} from '../../utils/transactionDisplay';
import { CancelPendingTransactionAction } from '../../components/CancelPendingTransactionAction';
import { FailureReasonBox } from '../../components/FailureReasonBox';
import type { TransactionData } from '../PastTransactions/utils/transactionUtils';

const InfoCircleIcon: React.FC = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M10.0003 8.95829C10.3455 8.95829 10.6253 9.23812 10.6253 9.58329V13.75C10.6253 14.0951 10.3455 14.375 10.0003 14.375C9.65515 14.375 9.37533 14.0951 9.37533 13.75V9.58329C9.37533 9.23812 9.65515 8.95829 10.0003 8.95829Z'
      fill='#0055FF'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M10.4268 5.78447C10.6833 6.01539 10.7041 6.41057 10.4732 6.66714L10.4649 6.6764C10.234 6.93296 9.83879 6.95376 9.58222 6.72285C9.32565 6.49194 9.30486 6.09676 9.53577 5.84019L9.5441 5.83093C9.77501 5.57436 10.1702 5.55356 10.4268 5.78447Z'
      fill='#0055FF'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M10.0003 2.29163C5.74313 2.29163 2.29199 5.74276 2.29199 9.99996C2.29199 14.2572 5.74313 17.7083 10.0003 17.7083C14.2575 17.7083 17.7087 14.2572 17.7087 9.99996C17.7087 5.74276 14.2575 2.29163 10.0003 2.29163ZM1.04199 9.99996C1.04199 5.05241 5.05277 1.04163 10.0003 1.04163C14.9479 1.04163 18.9587 5.05241 18.9587 9.99996C18.9587 14.9475 14.9479 18.9583 10.0003 18.9583C5.05277 18.9583 1.04199 14.9475 1.04199 9.99996Z'
      fill='#0055FF'
    />
  </svg>
);

const TimelineStep: React.FC<{
  title: string;
  date: string | null;
  isLast: boolean;
  status: string;
  isExpected?: boolean;
}> = ({ title, date, isLast, status, isExpected = false }) => {
  const isNullDate = !date || isExpected;
  const isSuccess = status === 'success';
  const isFailed = status === 'failed';
  const isPending = !isSuccess && !isFailed;

  const styles = isSuccess
    ? {
      circleFill: isNullDate ? '#AEB4C6' : '#089B19',
      iconColor: isNullDate ? '#AEB4C6' : '#089B19',
      lineColor: isNullDate ? '#AEB4C6' : '#089B19',
      textColor: isNullDate ? 'text-light/40' : 'text-main',
      dateColor: isNullDate ? 'text-light/30' : 'text-light/60',
    }
    : isFailed
      ? {
        circleFill: '#AEB4C6',
        iconColor: '#AEB4C6',
        lineColor: '#AEB4C6',
        textColor: 'text-light/40',
        dateColor: 'text-light/30',
      }
      : {
        circleFill: '#F59E0B',
        iconColor: '#F59E0B',
        lineColor: '#F59E0B',
        textColor: 'text-main',
        dateColor: 'text-light/60',
      };

  return (
    <div className={`relative flex items-start ${!isLast ? 'mb-8' : ''}`}>
      <div className='flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center'>
        <div className='relative'>
          <svg xmlns='http://www.w3.org/2000/svg' width='31' height='32' fill='none'>
            <circle cx='15.5' cy='16' r='15.5' fill={styles.circleFill} fillOpacity='0.1' />
          </svg>
          <div className='absolute inset-0 flex items-center justify-center'>
            {isFailed ? (
              <X className='w-4 h-4' style={{ color: styles.iconColor }} strokeWidth={3} />
            ) : isPending ? (
              <Clock className='w-4 h-4' style={{ color: styles.iconColor }} strokeWidth={2} />
            ) : (
              <Check className='w-4 h-4' style={{ color: styles.iconColor }} strokeWidth={3} />
            )}
          </div>
        </div>
      </div>
      {!isLast && (
        <div
          className='absolute w-[2px] h-[30px] top-7 left-3'
          style={{ backgroundColor: styles.lineColor }}
        />
      )}
      <div className='ml-4 flex flex-col'>
        <p className={`font-medium text-sm ${styles.textColor}`}>{title}</p>
        {(isSuccess || isPending) && (
          <p className={`text-sm ${styles.dateColor}`}>{date ?? 'Pending'}</p>
        )}
      </div>
    </div>
  );
};

const TxTimeline: React.FC<{
  timeline: TransactionTimeline | undefined;
  transaction: TransactionData;
  isLoading: boolean;
}> = ({ timeline, transaction, isLoading }) => {
  if (isLoading) {
    return (
      <div className='space-y-4'>
        <div className='h-6 w-48 bg-gray-200 animate-pulse rounded-md' />
        <div className='h-6 w-64 bg-gray-200 animate-pulse rounded-md' />
      </div>
    );
  }
  if (!timeline) return null;

  const status = transaction.status;

  if (status === 'failed') {
    return (
      <TimelineStep
        title='Payment method charged'
        date={
          timeline.paymentSuccess
            ? formatDateToDisplayShortLocalMonth(timeline.paymentSuccess)
            : null
        }
        isLast
        status={status}
      />
    );
  }

  const steps: { title: string; date: string | null; isExpected?: boolean }[] = [
    {
      title: 'Payment method charged',
      date: timeline.paymentSuccess
        ? formatDateToDisplayShortLocalMonth(timeline.paymentSuccess)
        : null,
    },
  ];

  // If refund exists, funds must have been received and sent
  const hasFundsReceived = timeline.batchCreatedAt || timeline.refundDate;
  const hasFundsSent = timeline.paymentSendToLandlord || timeline.refundDate;

  if (timeline.paymentSuccess) {
    steps.push({
      title: hasFundsReceived
        ? 'Funds received'
        : `Awaiting funds from ${transaction.cardType
          ? `${transaction.cardType} card`
          : getPaymentMode(
            transaction.paymentMethodName,
            transaction.cardCompany,
            transaction.cardType
          ) || 'payment method'
        }`,
      date: hasFundsReceived
        ? formatDateToDisplayShortLocalMonth(timeline.batchCreatedAt || timeline.refundDate!)
        : null,
    });
  }

  // Until funds are sent, show the expected date: batch creation time if a batch
  // exists, else the expectedPaymentSendDate.
  const expectedSendDate = timeline.batchCreatedAt || transaction.expectedPaymentSendDate;
  if (hasFundsSent) {
    steps.push({
      title: 'Funds sent to landlord',
      date: formatDateToDisplayShortLocalMonth(timeline.paymentSendToLandlord || timeline.refundDate!),
    });
  } else if (hasFundsReceived || (timeline.paymentSuccess && expectedSendDate)) {
    steps.push({
      title: hasFundsReceived ? 'Disbursement in progress' : 'Funds will be sent to landlord on',
      date: expectedSendDate ? formatDateToDisplayShortLocalMonth(expectedSendDate) : null,
      isExpected: true,
    });
  }

  if (timeline.paymentReportedToEquifax) {
    steps.push({
      title: 'Reported to Equifax',
      date: formatDateToDisplayShortLocalMonth(timeline.paymentReportedToEquifax),
    });
  }

  if (timeline.refundDate) {
    steps.push({
      title: 'Refund processed',
      date: formatDateToDisplayShortLocalMonth(timeline.refundDate),
    });
  }

  return (
    <div>
      {steps.map((step, i, arr) => (
        <TimelineStep
          key={step.title + (step.date || i)}
          title={step.title}
          date={step.date}
          isLast={i === arr.length - 1}
          status={status}
          isExpected={step.isExpected}
        />
      ))}
    </div>
  );
};

const TxStatus: React.FC<{ transaction: TransactionData }> = ({ transaction }) => {
  const cfg = isRefundTransaction(transaction)
    ? REFUND_STATUS_CONFIG
    : getStatusConfig(transaction.status, transaction.expectedPaymentSendDate);
  return (
    <div className='flex justify-between items-center w-full gap-4 border-b border-[#e0e6f0] pb-4 max-[400px]:flex-col max-[400px]:justify-center max-[400px]:items-center'>
      <span className='text-main text-sm font-medium'>Status</span>
      <div
        className={`justify-center items-center rounded-[12px] p-2 font-bold ${cfg.bgColorClass} ${cfg.colorClass}`}
      >
        {cfg.label}
      </div>
    </div>
  );
};

const TxSummary: React.FC<{ transaction: TransactionData }> = ({ transaction }) => (
  <div className='flex flex-col gap-4 w-full relative'>
    <div className='flex justify-between w-full'>
      <span className='text-light text-base'>Payment mode</span>
      <span className='text-main font-medium'>
        {getPaymentMode(
          transaction.paymentMethodName,
          transaction.cardCompany,
          transaction.cardType
        )}
      </span>
    </div>

    <div className='flex justify-between w-full'>
      <span className='text-light text-base'>Rent amount</span>
      <span className='text-main font-medium'>
        ${formatNumberWithDecimals(transaction.rentAmount)}
      </span>
    </div>

    <div className='flex justify-between w-full'>
      <div className='flex items-center gap-1'>
        <span className='text-light text-base'>Processing fee</span>
        <Whisper
          trigger='hover'
          placement='top'
          controlId='pay-rent-tx-fee-tooltip'
          speaker={
            <Tooltip>
              {Number(transaction.fixedFee) !== 0 && (
                <div style={{ marginTop: '12px' }}>
                  <div>
                    <strong>Fixed processing fee:</strong>
                  </div>
                  <div>${Number(transaction.fixedFee).toLocaleString()}</div>
                </div>
              )}
              {Number(transaction.percentageProcessingFee) !== 0 && (
                <div style={{ marginTop: '8px' }}>
                  <div>
                    <strong>Processing fee ({transaction.percentageRate}%):</strong>
                  </div>
                  <div>${transaction.percentageProcessingFee}</div>
                </div>
              )}
            </Tooltip>
          }
        >
          <Button appearance='subtle'>
            <InfoCircleIcon />
          </Button>
        </Whisper>
      </div>
      <span className='text-main font-medium'>
        ${transaction.processingFee.toLocaleString('en-US')}
      </span>
    </div>

    {transaction.discountAmount > 0 && (
      <div className='flex justify-between w-full'>
        <span className='text-light text-base'>Rent credit</span>
        <span className='text-success font-semibold text-base'>
          ${transaction.discountAmount?.toLocaleString('en-US')}
        </span>
      </div>
    )}

    {transaction.promoCodeDiscountAmount > 0 && (
      <div className='flex justify-between w-full'>
        <span className='text-light text-base'>Promo code discount</span>
        <span className='text-success font-semibold text-base'>
          ${transaction.promoCodeDiscountAmount?.toLocaleString('en-US')}
        </span>
      </div>
    )}

    <div className='flex justify-between w-full border-t border-[#E8EAF0] pt-4'>
      <span className='text-light text-base'>
        {isRefundTransaction(transaction) ? 'Total amount refunded' : 'Total amount paid'}
      </span>
      <span
        className={`font-bold text-base ${isRefundTransaction(transaction) ? 'text-success' : 'text-primary'}`}
      >
        {isRefundTransaction(transaction) ? '+' : ''}${Number(transaction.totalAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
      </span>
    </div>
  </div>
);

const useTimeline = (authToken: string | undefined) => {
  const [timeline, setTimeline] = useState<TransactionTimeline | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const fetchTimeline = useCallback(
    async (transactionId: string | null) => {
      if (!transactionId || !authToken) return;
      setIsLoading(true);
      try {
        const response = await getTransactionTimeline(authToken, transactionId);
        setTimeline(response.data);
      } catch (err) {
        console.error('Error fetching timeline', err);
      } finally {
        setIsLoading(false);
      }
    },
    [authToken]
  );

  return { timeline, isLoading, fetchTimeline };
};

export interface PayRentTransactionDetailPanelProps {
  transaction: TransactionData;
  onClose: () => void;
  onCancelled?: () => void;
}

/** Timeline + summary UI for Rent History, rendered inside global `<Modal id="transaction-detail">`. */
const PayRentTransactionDetailPanel: React.FC<PayRentTransactionDetailPanelProps> = ({
  transaction,
  onClose,
  onCancelled,
}) => {
  const { auth } = useAuth();
  const {
    timeline,
    isLoading: timelineLoading,
    fetchTimeline,
  } = useTimeline(auth?.data.token);

  useEffect(() => {
    if (transaction?.transactionId) fetchTimeline(transaction.transactionId);
  }, [transaction?.transactionId, fetchTimeline]);

  return (
    <motion.div
      className='relative w-full max-w-[480px] max-h-[90vh] bg-white rounded-[12px] flex flex-col overflow-hidden shadow-xl'
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      <div className='flex justify-between p-4 border-b border-[#DEE2EF] items-center h-[60px] flex-shrink-0'>
        <span className='text-main text-[18px]'>Transaction detail</span>
        <X
          className='cursor-pointer text-gray-500 hover:text-gray-700'
          size={24}
          onClick={onClose}
        />
      </div>

      <div className='overflow-y-auto flex-1 p-4 relative'>
        <motion.div
          key={transaction.transactionId}
          initial={{ y: 50, opacity: 0.5 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 70, damping: 20 }}
          className='flex flex-col py-8 px-6 gap-10 relative z-10'
        >
          <div className='relative'>
            <div className='flex flex-col px-[20px] py-[24px] items-start rounded-[20px] gap-6 bg-[#f4f5fb]'>
              <div className='w-full flex flex-col gap-6'>
                <TxStatus transaction={transaction} />
                {isRefundTransaction(transaction) && (
                  <p className='text-sm text-light'>
                    This is a refund of a previous payment. The amount was returned to you and is not a new charge.
                  </p>
                )}
                {transaction.status?.toLowerCase() === 'failed' && (
                  <FailureReasonBox
                    message={
                      transaction.formattedErrorMessage || transaction.errorMessages
                    }
                  />
                )}
                <TxTimeline
                  timeline={timeline}
                  transaction={transaction}
                  isLoading={timelineLoading}
                />
              </div>
            </div>
          </div>

          <TxSummary transaction={transaction} />

          <CancelPendingTransactionAction
            tenantTransactionId={transaction.transactionId}
            status={transaction.status}
            onCancelled={() => {
              onCancelled?.();
              onClose();
            }}
          />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default PayRentTransactionDetailPanel;
